import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { WE_PEDIDOS_LOGO_URL as mozzoIcon } from "../../services/local/institutionalBranding";
import WeCustomerAccess from "../we-pedidos/WeCustomerAccess";
import WeCustomerAccountDialog from "../we-pedidos/WeCustomerAccountDialog";
import WePedidosAndroidLauncher from "../we-pedidos/WePedidosAndroidLauncher";
import useWePedidosBranding from "../we-pedidos/useWePedidosBranding";
import {
  configureNativeCustomerBackground,
  hasActiveWeCustomerOrders,
  notifyCustomerOrderChanges,
  notifyCustomerTrackingChange,
  stopNativeCustomerBackground,
} from "../../services/local/weAppNotifications";
import {
  buildWeRestaurantSections,
  findWeProductMatches,
  filterWeRestaurants,
  prepareWeRestaurants,
} from "../../services/local/wePedidosDiscovery";
import {
  discoverWeRestaurants,
  getWeRestaurantRatingEligibility,
  loadWeCustomerOrders,
  loadWeDeliveryTracking,
  partitionWeCustomerOrders,
  requestWeCustomerOrderChange,
  resolveWePedidosNavigationUrl,
  submitWeProductRating,
  submitWeRestaurantRating,
  WE_PEDIDOS_ORDERS_UPDATED_EVENT,
  WE_PEDIDOS_PREVIEW_UPDATED_EVENT,
} from "../../services/supabase/wePedidosService";
import {
  getWeCustomerAuthState,
  registerWeCustomerBackgroundDevice,
  revokeWeCustomerBackgroundDevice,
  saveWeCustomerAddress,
  signOutWeCustomer,
  subscribeToWeCustomerAuth,
  subscribeToWeCustomerOrderChanges,
} from "../../services/supabase/weCustomerAuthService";
import { subscribeToLocalWeRiderUpdates } from "../../services/supabase/weRiderService";
import "../../styles/WePedidos.css";

const LOCATION_STORAGE_PREFIX = "mozzoapp:we-pedidos-location:";
let nativeCustomerBackgroundKey = "";

const syncNativeCustomerBackground = async ({
  accessToken,
  userId,
  orders,
}) => {
  if (
    typeof window === "undefined" ||
    typeof window.MozzoNotifications?.configureCustomerBackground !== "function"
  ) {
    return;
  }

  const active = hasActiveWeCustomerOrders(orders);
  if (!active) {
    if (nativeCustomerBackgroundKey) {
      nativeCustomerBackgroundKey = "";
      stopNativeCustomerBackground();
      await revokeWeCustomerBackgroundDevice({ accessToken }).catch(() => null);
    }
    return;
  }

  const activeOrderIds = (Array.isArray(orders) ? orders : [])
    .filter((order) => hasActiveWeCustomerOrders([order]))
    .map((order) => String(order?.id || ""))
    .filter(Boolean)
    .sort();
  const registrationKey = `${userId}:${activeOrderIds.join(",")}`;
  if (!userId || nativeCustomerBackgroundKey === registrationKey) return;
  nativeCustomerBackgroundKey = registrationKey;
  try {
    const configuration = await registerWeCustomerBackgroundDevice({ accessToken });
    if (nativeCustomerBackgroundKey === registrationKey && configuration) {
      configureNativeCustomerBackground(configuration, orders);
    }
  } catch {
    if (nativeCustomerBackgroundKey === registrationKey) {
      nativeCustomerBackgroundKey = "";
    }
  }
};
const moneyFormatter = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  maximumFractionDigits: 0,
});
const orderDateFormatter = new Intl.DateTimeFormat("es-AR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const initialFilters = {
  search: "",
  foodType: "",
  maxDistanceKm: "",
  minimumRating: "",
  maxEtaMinutes: "",
  priceLevel: "",
  paymentMethod: "",
  promotionsOnly: false,
  freeDeliveryOnly: false,
};

function DeliveryVerificationCode({ code }) {
  if (!/^\d{6}$/.test(String(code || ""))) return null;

  return (
    <section className="we-delivery-code" aria-label="Código para recibir el pedido">
      <div>
        <span>Código para recibir tu pedido</span>
        <strong>{code}</strong>
      </div>
      <p>Dáselo al rider únicamente cuando tengas el pedido en tus manos.</p>
    </section>
  );
}

function WeOrderRestaurantRating({ order, accessToken, accountKey, onSubmitted }) {
  const [submittingRating, setSubmittingRating] = useState(0);
  const [error, setError] = useState("");
  const eligibility = getWeRestaurantRatingEligibility(order);
  const review = order?.review;

  if (!eligibility.delivered) return null;

  const handleRating = async (rating) => {
    setSubmittingRating(rating);
    setError("");
    try {
      const response = await submitWeRestaurantRating({
        accessToken,
        accountKey,
        orderId: order.id,
        rating,
      });
      onSubmitted(order.id, response?.review || { rating });
    } catch (requestError) {
      setError(requestError?.message || "No pudimos guardar tu calificacion.");
    } finally {
      setSubmittingRating(0);
    }
  };

  if (review?.rating) {
    return (
      <section className="we-order-review is-complete" aria-label="Tu calificación del restaurante">
        <div>
          <strong>Tu calificación</strong>
          <span>Gracias por compartir tu experiencia.</span>
        </div>
        <div className="we-order-review-stars is-readonly" aria-label={`${review.rating} de 5 estrellas`}>
          {[1, 2, 3, 4, 5].map((star) => (
            <span key={star} className={star <= Number(review.rating) ? "is-selected" : ""} aria-hidden="true">★</span>
          ))}
        </div>
      </section>
    );
  }

  if (!eligibility.canReview) {
    return (
      <p className="we-order-review-waiting">
        Podrás calificar al restaurante 30 minutos después de realizar el pedido.
      </p>
    );
  }

  return (
    <section className="we-order-review" aria-label="Calificar restaurante">
      <div>
        <strong>¿Cómo estuvo el restaurante?</strong>
        <span>Calificá tu experiencia de 1 a 5 estrellas.</span>
      </div>
      <div className="we-order-review-stars">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            title={`Calificar con ${star} ${star === 1 ? "estrella" : "estrellas"}`}
            aria-label={`Calificar con ${star} ${star === 1 ? "estrella" : "estrellas"}`}
            onClick={() => void handleRating(star)}
            disabled={Boolean(submittingRating)}
            className={submittingRating >= star ? "is-selected" : ""}
          >
            ★
          </button>
        ))}
      </div>
      {error ? <p className="we-order-review-error" role="alert">{error}</p> : null}
    </section>
  );
}

function WeOrderProductRatings({ order, accessToken, accountKey, onSubmitted }) {
  const [submittingItemId, setSubmittingItemId] = useState("");
  const [selectedRating, setSelectedRating] = useState(0);
  const [error, setError] = useState("");
  const eligibility = getWeRestaurantRatingEligibility(order);
  const items = Array.isArray(order?.items) ? order.items : [];
  const delayElapsed =
    Boolean(eligibility.reviewAvailableAt) &&
    new Date(eligibility.reviewAvailableAt).getTime() <= Date.now();

  if (!eligibility.delivered || !delayElapsed || !items.length) return null;

  const handleRating = async (item, rating) => {
    setSubmittingItemId(item.id);
    setSelectedRating(rating);
    setError("");
    try {
      const response = await submitWeProductRating({
        accessToken,
        accountKey,
        orderId: order.id,
        orderItemId: item.id,
        rating,
      });
      onSubmitted(
        order.id,
        item.id,
        response?.review || { orderItemId: item.id, rating }
      );
    } catch (requestError) {
      setError(
        requestError?.message || "No pudimos guardar la calificacion del plato."
      );
    } finally {
      setSubmittingItemId("");
      setSelectedRating(0);
    }
  };

  return (
    <section className="we-order-product-reviews" aria-label="Calificar platos del pedido">
      <div className="we-order-product-reviews-heading">
        <strong>¿Cómo estuvieron tus platos?</strong>
        <span>Calificá cada producto que recibiste.</span>
      </div>
      <ul>
        {items.map((item) => {
          const reviewRating = Number(item?.review?.rating || 0);
          const isSubmitting = submittingItemId === item.id;
          return (
            <li key={item.id}>
              <div className="we-order-product-review-name">
                <strong>{item.productName}</strong>
                <span>{Number(item.quantity || 0)} unidad{Number(item.quantity || 0) === 1 ? "" : "es"}</span>
              </div>
              <div
                className={`we-order-review-stars${reviewRating ? " is-readonly" : ""}`}
                aria-label={
                  reviewRating
                    ? `${reviewRating} de 5 estrellas para ${item.productName}`
                    : `Calificar ${item.productName}`
                }
              >
                {[1, 2, 3, 4, 5].map((star) =>
                  reviewRating ? (
                    <span
                      key={star}
                      className={star <= reviewRating ? "is-selected" : ""}
                      aria-hidden="true"
                    >
                      ★
                    </span>
                  ) : (
                    <button
                      key={star}
                      type="button"
                      title={`${star} ${star === 1 ? "estrella" : "estrellas"} para ${item.productName}`}
                      aria-label={`${star} ${star === 1 ? "estrella" : "estrellas"} para ${item.productName}`}
                      onClick={() => void handleRating(item, star)}
                      disabled={Boolean(submittingItemId)}
                      className={isSubmitting && selectedRating >= star ? "is-selected" : ""}
                    >
                      ★
                    </button>
                  )
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {error ? <p className="we-order-review-error" role="alert">{error}</p> : null}
    </section>
  );
}

const CUSTOMER_REQUEST_TERMINAL_STATUSES = new Set([
  "cancelled",
  "delivered",
  "picked_up",
  "refunded",
  "rejected",
]);

function WeOrderCustomerRequest({
  order,
  accessToken,
  accountKey,
  onSubmitted,
}) {
  const [requestType, setRequestType] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const customerRequest = order?.customerRequest;
  const isPending = customerRequest?.status === "pending";
  const terminal = [order?.status, order?.trackingStatus]
    .map((status) => String(status || "").toLowerCase())
    .some((status) => CUSTOMER_REQUEST_TERMINAL_STATUSES.has(status));

  if (terminal && !customerRequest) return null;

  const requestLabel =
    customerRequest?.type === "cancellation"
      ? "Cancelación solicitada"
      : "Modificación solicitada";
  const responseLabel = {
    accepted: "Solicitud aceptada por el restaurante",
    rejected: "El restaurante no pudo aceptar la solicitud",
    resolved: "Solicitud atendida por el restaurante",
  }[customerRequest?.status];

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const response = await requestWeCustomerOrderChange({
        accessToken,
        accountKey,
        orderId: order.id,
        requestType,
        message,
      });
      onSubmitted(order.id, response?.customerRequest);
      setRequestType("");
      setMessage("");
    } catch (requestError) {
      setError(
        requestError?.message ||
          "No pudimos enviar la solicitud al restaurante."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (isPending) {
    return (
      <section className="we-order-customer-request is-pending" aria-live="polite">
        <strong>{requestLabel}</strong>
        <p>{customerRequest.message}</p>
        <small>
          El pedido todavía no cambió. El restaurante debe revisar y confirmar tu solicitud.
        </small>
      </section>
    );
  }

  return (
    <section className="we-order-customer-request">
      {responseLabel ? (
        <p className="we-order-request-response"><strong>{responseLabel}</strong></p>
      ) : null}
      {!terminal && !requestType ? (
        <div className="we-order-request-actions">
          <button type="button" onClick={() => setRequestType("modification")}>
            Solicitar modificación
          </button>
          <button
            type="button"
            className="is-danger"
            onClick={() => setRequestType("cancellation")}
          >
            Solicitar cancelación
          </button>
        </div>
      ) : null}
      {!terminal && requestType ? (
        <div className="we-order-request-form">
          <strong>
            {requestType === "cancellation"
              ? "¿Por qué querés cancelar?"
              : "¿Qué necesitás modificar?"}
          </strong>
          <p>
            Es una solicitud: el restaurante debe confirmarla. Si el pedido ya está en preparación, puede no ser posible realizar el cambio.
          </p>
          <textarea
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            maxLength={1000}
            rows={3}
            placeholder={
              requestType === "cancellation"
                ? "Contale al restaurante el motivo"
                : "Ej: quitar tomate de la milanesa"
            }
            disabled={submitting}
          />
          <div>
            <button
              type="button"
              onClick={() => {
                setRequestType("");
                setMessage("");
                setError("");
              }}
              disabled={submitting}
            >
              Volver
            </button>
            <button
              type="button"
              className={requestType === "cancellation" ? "is-danger" : "is-primary"}
              onClick={() => void handleSubmit()}
              disabled={submitting || message.trim().length < 5}
            >
              {submitting ? "Enviando..." : "Enviar solicitud"}
            </button>
          </div>
          {error ? <p className="we-order-review-error" role="alert">{error}</p> : null}
        </div>
      ) : null}
    </section>
  );
}

function useWeCustomerOrderNotifications(authState) {
  const ordersRef = useRef(null);
  const accessToken = authState.session?.access_token || "";
  const accountKey = authState.profile?.userId || authState.profile?.email || "";

  useEffect(() => {
    ordersRef.current = null;
    if (!authState.session || !accountKey) return undefined;

    let mounted = true;
    const refresh = async () => {
      try {
        const response = await loadWeCustomerOrders({ accessToken, accountKey });
        if (!mounted) return;
        const nextOrders = Array.isArray(response?.orders) ? response.orders : [];
        notifyCustomerOrderChanges(ordersRef.current, nextOrders);
        ordersRef.current = nextOrders;
        void syncNativeCustomerBackground({
          accessToken,
          userId: authState.session?.user?.id || "",
          orders: nextOrders,
        });
      } catch {
        // Una interrupcion temporal no debe borrar el ultimo estado conocido.
      }
    };

    void refresh();
    const intervalId = window.setInterval(refresh, 8000);
    window.addEventListener("online", refresh);
    return () => {
      mounted = false;
      window.clearInterval(intervalId);
      window.removeEventListener("online", refresh);
    };
  }, [accessToken, accountKey, authState.session]);
}

const getLocationStorageKey = (customerId) =>
  `${LOCATION_STORAGE_PREFIX}${customerId || "anonymous"}`;

const readSavedLocation = (customerId) => {
  if (typeof window === "undefined") return null;
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(getLocationStorageKey(customerId)) || "null"
    );
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const saveLocation = (location, customerId) => {
  if (typeof window === "undefined" || !location) return;
  window.localStorage.setItem(
    getLocationStorageKey(customerId),
    JSON.stringify(location)
  );
};

const formatDistance = (distanceKm) => {
  if (
    distanceKm === null ||
    distanceKm === undefined ||
    (typeof distanceKm === "string" && !distanceKm.trim())
  ) {
    return "En tu ciudad";
  }
  const distance = Number(distanceKm);
  if (!Number.isFinite(distance)) return "En tu ciudad";
  return distance < 1
    ? `${Math.max(100, Math.round(distance * 1000 / 100) * 100)} m`
    : `${distance.toFixed(1).replace(".", ",")} km`;
};

const formatRating = (rating) => Number(rating || 0).toFixed(1).replace(".", ",");

const priceLevelLabel = (level) => "$".repeat(Math.max(1, Math.min(4, Number(level || 2))));

function RestaurantVisual({ restaurant }) {
  const [imageFailed, setImageFailed] = useState(false);
  const hasCover = Boolean(restaurant.coverUrl) && !imageFailed;

  return (
    <div
      className="we-restaurant-visual"
      style={{ "--we-card-accent": restaurant.accentColor || "#ff5a1f" }}
    >
      {hasCover ? (
        <img
          src={restaurant.coverUrl}
          alt={`Vista de ${restaurant.name}`}
          onError={() => setImageFailed(true)}
        />
      ) : (
        <div className="we-restaurant-fallback">
          <img
            className={restaurant.logoUrl ? "" : "we-logo-image"}
            src={restaurant.logoUrl || mozzoIcon}
            alt=""
          />
          <span>{restaurant.foodTypes?.[0] || "Delivery"}</span>
        </div>
      )}
      <div className={`we-restaurant-eta${restaurant.isOpen ? "" : " is-closed"}`}>
        {restaurant.isOpen ? `${restaurant.estimatedMinutes || 35} min` : "Cerrado"}
      </div>
      {restaurant.promotionCount > 0 ? (
        <div className="we-restaurant-promo">
          {restaurant.promotionCount === 1 ? "1 promo" : `${restaurant.promotionCount} promos`}
        </div>
      ) : null}
    </div>
  );
}

function RestaurantCard({ restaurant, onOpen, compact = false }) {
  return (
    <article className={`we-restaurant-card${compact ? " is-compact" : ""}`}>
      <button
        type="button"
        className={`we-restaurant-card-action${restaurant.isOpen ? "" : " is-closed"}`}
        onClick={() => onOpen(restaurant)}
        aria-label={`Abrir el menú de ${restaurant.name}`}
      >
        <RestaurantVisual restaurant={restaurant} />
        <div className="we-restaurant-info">
          <div className="we-restaurant-title-row">
            <h3>{restaurant.name}</h3>
            <span className="we-rating" aria-label={`${restaurant.ratingAverage} estrellas`}>
              <span aria-hidden="true">★</span> {formatRating(restaurant.ratingAverage)}
            </span>
          </div>
          <p>{restaurant.foodTypes?.join(" · ") || restaurant.description || "Menú disponible"}</p>
          <div className="we-restaurant-meta">
            <span>{formatDistance(restaurant.distanceKm)}</span>
            <span>{priceLevelLabel(restaurant.priceLevel)}</span>
            <span>
              {Number(restaurant.deliveryFee || 0) > 0
                ? `Envío ${moneyFormatter.format(restaurant.deliveryFee)}`
                : "Envío gratis"}
            </span>
          </div>
        </div>
      </button>
    </article>
  );
}

function RestaurantSection({ title, description, restaurants, onOpen }) {
  if (!restaurants.length) return null;

  return (
    <section className="we-section">
      <div className="we-section-heading">
        <div>
          <h2>{title}</h2>
          {description ? <p>{description}</p> : null}
        </div>
        <span>{restaurants.length}</span>
      </div>
      <div className="we-horizontal-list">
        {restaurants.slice(0, 10).map((restaurant) => (
          <RestaurantCard
            key={`${title}-${restaurant.id}`}
            restaurant={restaurant}
            onOpen={onOpen}
            compact
          />
        ))}
      </div>
    </section>
  );
}

function ProductSearchCard({ product, onOpen }) {
  const [imageFailed, setImageFailed] = useState(false);
  return (
    <article className="we-product-result-card">
      <button type="button" onClick={() => onOpen(product)}>
        <div className="we-product-result-image">
          {product.imageUrl && !imageFailed ? (
            <img
              src={product.imageUrl}
              alt={product.name}
              onError={() => setImageFailed(true)}
            />
          ) : (
            <span aria-hidden="true">{product.name?.slice(0, 1) || "P"}</span>
          )}
          {!product.restaurantIsOpen ? <b>Local cerrado</b> : null}
        </div>
        <div className="we-product-result-info">
          <small>{product.categoryName || "Plato"}</small>
          <h3>{product.name}</h3>
          <p>{product.restaurantName}</p>
          <div className="we-product-result-footer">
            <strong>{moneyFormatter.format(Number(product.price || 0))}</strong>
            {Number(product.ratingCount || 0) > 0 ? (
              <span aria-label={`${product.ratingAverage} estrellas en ${product.ratingCount} calificaciones`}>
                <b aria-hidden="true">★</b> {formatRating(product.ratingAverage)} ({product.ratingCount})
              </span>
            ) : (
              <span>Sin calificaciones</span>
            )}
          </div>
        </div>
      </button>
    </article>
  );
}

function LocationDialog({ onClose, onUseCurrentLocation, onUseManualLocation, locating, error, initialLocation }) {
  const [city, setCity] = useState(initialLocation?.city || "Corrientes");
  const [address, setAddress] = useState(initialLocation?.address || "");

  const submitManual = (event) => {
    event.preventDefault();
    onUseManualLocation({ city, address });
  };

  return (
    <div className="we-dialog-backdrop" role="presentation">
      <section className="we-dialog" role="dialog" aria-modal="true" aria-labelledby="we-location-title">
        {onClose ? (
          <button type="button" className="we-icon-button we-dialog-close" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        ) : null}
        <img className="we-dialog-logo we-logo-image" src={mozzoIcon} alt="" />
        <p className="we-eyebrow">Tu zona de entrega</p>
        <h2 id="we-location-title">¿Dónde querés recibir el pedido?</h2>
        <p className="we-dialog-copy">
          Usamos tu ubicación para mostrar solamente locales que entregan en tu ciudad.
        </p>
        <button
          type="button"
          className="we-primary-button"
          onClick={onUseCurrentLocation}
          disabled={locating}
        >
          {locating ? "Buscando ubicación..." : "Usar mi ubicación actual"}
        </button>
        <div className="we-dialog-separator"><span>o ingresala</span></div>
        <form className="we-location-form" onSubmit={submitManual}>
          <label>
            Ciudad
            <input value={city} onChange={(event) => setCity(event.target.value)} placeholder="Ej. Corrientes" required />
          </label>
          <label>
            Calle y altura <small>se guarda en tu cuenta</small>
            <input value={address} onChange={(event) => setAddress(event.target.value)} placeholder="Calle y altura" />
          </label>
          {error ? <p className="we-inline-error" role="alert">{error}</p> : null}
          <button type="submit" className="we-secondary-button" disabled={locating}>Buscar en esta zona</button>
        </form>
      </section>
    </div>
  );
}

function FiltersPanel({ filters, setFilters, foodTypes, onClose, onClear }) {
  const update = (key, value) => setFilters((current) => ({ ...current, [key]: value }));

  return (
    <aside className="we-filters" aria-label="Filtros de restaurantes">
      <div className="we-filters-heading">
        <h2>Filtros</h2>
        <button type="button" className="we-icon-button" onClick={onClose} aria-label="Cerrar filtros">×</button>
      </div>
      <div className="we-filter-grid">
        <label>
          Tipo de comida
          <select value={filters.foodType} onChange={(event) => update("foodType", event.target.value)}>
            <option value="">Todos</option>
            {foodTypes.map((foodType) => <option key={foodType} value={foodType}>{foodType}</option>)}
          </select>
        </label>
        <label>
          Distancia
          <select value={filters.maxDistanceKm} onChange={(event) => update("maxDistanceKm", event.target.value)}>
            <option value="">Sin límite</option>
            <option value="3">Hasta 3 km</option>
            <option value="5">Hasta 5 km</option>
            <option value="10">Hasta 10 km</option>
          </select>
        </label>
        <label>
          Valoración
          <select value={filters.minimumRating} onChange={(event) => update("minimumRating", event.target.value)}>
            <option value="">Todas</option>
            <option value="4">4 o más</option>
            <option value="4.5">4,5 o más</option>
          </select>
        </label>
        <label>
          Tiempo de entrega
          <select value={filters.maxEtaMinutes} onChange={(event) => update("maxEtaMinutes", event.target.value)}>
            <option value="">Cualquier tiempo</option>
            <option value="30">Hasta 30 min</option>
            <option value="45">Hasta 45 min</option>
            <option value="60">Hasta 60 min</option>
          </select>
        </label>
        <label>
          Precio
          <select value={filters.priceLevel} onChange={(event) => update("priceLevel", event.target.value)}>
            <option value="">Todos</option>
            <option value="1">$</option>
            <option value="2">$$</option>
            <option value="3">$$$</option>
            <option value="4">$$$$</option>
          </select>
        </label>
        <label>
          Medio de pago
          <select value={filters.paymentMethod} onChange={(event) => update("paymentMethod", event.target.value)}>
            <option value="">Todos</option>
            <option value="Efectivo">Efectivo</option>
            <option value="Mercado Pago">Mercado Pago</option>
          </select>
        </label>
      </div>
      <div className="we-checkboxes">
        <label><input type="checkbox" checked={filters.promotionsOnly} onChange={(event) => update("promotionsOnly", event.target.checked)} /> Con promociones</label>
        <label><input type="checkbox" checked={filters.freeDeliveryOnly} onChange={(event) => update("freeDeliveryOnly", event.target.checked)} /> Envío gratis</label>
      </div>
      <div className="we-filter-actions">
        <button type="button" className="we-text-button" onClick={onClear}>Limpiar</button>
        <button type="button" className="we-primary-button" onClick={onClose}>Ver resultados</button>
      </div>
    </aside>
  );
}

export default function WePedidos() {
  useWePedidosBranding();
  const navigate = useNavigate();
  const [authState, setAuthState] = useState({
    loading: true,
    session: null,
    profile: null,
    error: "",
  });
  const [location, setLocation] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [searchedRestaurants, setSearchedRestaurants] = useState(null);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [source, setSource] = useState("");
  const [trackingCode, setTrackingCode] = useState("");
  const [accountOpen, setAccountOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const searchRequestRef = useRef(0);
  useWeCustomerOrderNotifications(authState);

  const refreshAuthState = useCallback(async () => {
    try {
      const nextState = await getWeCustomerAuthState();
      setAuthState({ ...nextState, loading: false, error: "" });
    } catch (requestError) {
      setAuthState({
        loading: false,
        session: null,
        profile: null,
        error:
          requestError?.message ||
          "No pudimos recuperar tu cuenta de We! pedidos.",
      });
    }
  }, []);

  useEffect(() => {
    void refreshAuthState();
    return subscribeToWeCustomerAuth(() => {
      void refreshAuthState();
    });
  }, [refreshAuthState]);

  const customerId = authState.profile?.userId || authState.session?.user?.id || "";
  const defaultAddress = useMemo(
    () =>
      authState.profile?.addresses?.find((address) => address.isDefault) ||
      authState.profile?.addresses?.[0] ||
      null,
    [authState.profile]
  );

  const loadDiscovery = useCallback(async (request) => {
    setLoading(true);
    setError("");
    try {
      const response = await discoverWeRestaurants(request);
      setLocation(response.location);
      setRestaurants(Array.isArray(response.restaurants) ? response.restaurants : []);
      setSource(response.source || "live");
      saveLocation(response.location, customerId);
      setLocationOpen(false);
    } catch (requestError) {
      setError(requestError?.message || "No pudimos buscar restaurantes en esta zona.");
    } finally {
      setLoading(false);
      setLocating(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (authState.loading || !authState.session) return;
    const savedLocation = readSavedLocation(customerId);
    if (savedLocation?.city) {
      loadDiscovery(savedLocation);
    } else if (defaultAddress?.city) {
      loadDiscovery({
        city: defaultAddress.city,
        address:
          defaultAddress.addressLine ||
          `${defaultAddress.street || ""} ${defaultAddress.streetNumber || ""}`.trim(),
        latitude: defaultAddress.latitude,
        longitude: defaultAddress.longitude,
      });
    } else {
      setLocationOpen(true);
    }
  }, [authState.loading, authState.session, customerId, defaultAddress, loadDiscovery]);

  useEffect(() => {
    const refreshLocalPreview = () => {
      const savedLocation = readSavedLocation(customerId);
      if (savedLocation?.city) void loadDiscovery(savedLocation);
    };
    const refreshFromStorage = (event) => {
      if (event.key === "mozzoapp:we-pedidos-preview-restaurants") {
        refreshLocalPreview();
      }
    };

    window.addEventListener(
      WE_PEDIDOS_PREVIEW_UPDATED_EVENT,
      refreshLocalPreview
    );
    window.addEventListener("storage", refreshFromStorage);
    return () => {
      window.removeEventListener(
        WE_PEDIDOS_PREVIEW_UPDATED_EVENT,
        refreshLocalPreview
      );
      window.removeEventListener("storage", refreshFromStorage);
    };
  }, [customerId, loadDiscovery]);

  useEffect(() => {
    const search = filters.search.trim();
    if (!location?.city || search.length < 2) {
      searchRequestRef.current += 1;
      setSearchedRestaurants(null);
      setSearchingProducts(false);
      return undefined;
    }

    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;
    const timeoutId = window.setTimeout(async () => {
      setSearchingProducts(true);
      try {
        const response = await discoverWeRestaurants({ ...location, search });
        if (searchRequestRef.current !== requestId) return;
        setSearchedRestaurants(
          Array.isArray(response?.restaurants) ? response.restaurants : []
        );
      } catch {
        if (searchRequestRef.current === requestId) {
          setSearchedRestaurants(null);
        }
      } finally {
        if (searchRequestRef.current === requestId) {
          setSearchingProducts(false);
        }
      }
    }, 320);

    return () => window.clearTimeout(timeoutId);
  }, [filters.search, location]);

  const discoveryRestaurants = searchedRestaurants ?? restaurants;

  const preparedRestaurants = useMemo(
    () => prepareWeRestaurants({
      restaurants: discoveryRestaurants,
      city: location?.city,
      latitude: location?.latitude,
      longitude: location?.longitude,
    }),
    [discoveryRestaurants, location]
  );
  const filteredRestaurants = useMemo(
    () => filterWeRestaurants(preparedRestaurants, filters),
    [preparedRestaurants, filters]
  );
  const sections = useMemo(
    () => buildWeRestaurantSections(filteredRestaurants),
    [filteredRestaurants]
  );
  const foodTypes = useMemo(
    () => [...new Set(preparedRestaurants.flatMap((restaurant) => restaurant.foodTypes || []))].sort((left, right) => left.localeCompare(right, "es")),
    [preparedRestaurants]
  );
  const activeFilterCount = Object.entries(filters).filter(([key, value]) =>
    key === "search" ? false : typeof value === "boolean" ? value : Boolean(value)
  ).length;
  const productMatches = useMemo(
    () => findWeProductMatches(preparedRestaurants, filters.search),
    [preparedRestaurants, filters.search]
  );
  const hasSearch = Boolean(filters.search.trim());

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Este dispositivo no permite detectar la ubicación. Ingresa tu ciudad manualmente.");
      return;
    }
    setLocating(true);
    setError("");
    navigator.geolocation.getCurrentPosition(
      (position) => loadDiscovery({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      }),
      () => {
        setLocating(false);
        setError("No pudimos acceder a tu ubicación. Puedes ingresar la ciudad debajo.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  const useManualLocation = async ({ city, address }) => {
    setLocating(true);
    setError("");
    try {
      if (address || city) {
        await saveWeCustomerAddress({
          id: defaultAddress?.id,
          label: defaultAddress?.label || "Casa",
          addressLine: address,
          street: defaultAddress?.street || "",
          streetNumber: defaultAddress?.streetNumber || "",
          city,
          province: defaultAddress?.province || "",
          isDefault: true,
        });
        await refreshAuthState();
      }
      await loadDiscovery({ city, address });
    } catch (requestError) {
      setError(
        requestError?.message || "No pudimos guardar la zona de entrega."
      );
      setLocating(false);
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    setError("");
    try {
      await signOutWeCustomer();
      setAccountOpen(false);
      setLocation(null);
      setRestaurants([]);
      await refreshAuthState();
    } catch (requestError) {
      setError(requestError?.message || "No pudimos cerrar la sesión.");
    } finally {
      setSigningOut(false);
    }
  };

  const openRestaurant = (restaurant) => {
    if (restaurant.menuUrl) {
      const menuUrl = resolveWePedidosNavigationUrl(restaurant.menuUrl);
      if (String(menuUrl).startsWith("/")) {
        navigate(menuUrl);
      } else {
        window.location.assign(menuUrl);
      }
      return;
    }
    setNotice("El menú público de esta sucursal todavía no está disponible.");
  };

  const openProduct = (product) => {
    if (!product?.menuUrl) return;
    const menuUrl = new URL(
      resolveWePedidosNavigationUrl(product.menuUrl),
      window.location.origin
    );
    menuUrl.searchParams.set("entry", "we_pedidos");
    menuUrl.searchParams.set("dish", product.name || "");
    menuUrl.searchParams.set("product", product.id || "");
    if (menuUrl.origin === window.location.origin) {
      navigate(`${menuUrl.pathname}${menuUrl.search}${menuUrl.hash}`);
    } else {
      window.location.assign(menuUrl.toString());
    }
  };

  const submitTracking = (event) => {
    event.preventDefault();
    const token = trackingCode.trim();
    if (!token) return;
    navigate(`/we-pedidos/seguimiento/${encodeURIComponent(token)}`);
  };

  if (authState.loading) {
    return (
      <main className="we-app-shell">
        <section className="we-loading" aria-live="polite">
          <img className="we-logo-image" src={mozzoIcon} alt="" />
          <p>Preparando tu cuenta de We! pedidos...</p>
        </section>
      </main>
    );
  }

  if (!authState.session) {
    return (
      <>
        <WeCustomerAccess onAuthenticated={refreshAuthState} />
        {authState.error ? (
          <p className="we-access-global-error" role="alert">
            {authState.error}
          </p>
        ) : null}
      </>
    );
  }

  return (
    <main className="we-app-shell">
      <header className="we-topbar">
        <Link className="we-brand" to="/we-pedidos" aria-label="We! pedidos, inicio">
          <img className="we-logo-image" src={mozzoIcon} alt="" />
          <span><strong>We!</strong> pedidos<small>por MozzoApp</small></span>
        </Link>
        <div className="we-topbar-actions">
          <button type="button" className="we-location-button" onClick={() => setLocationOpen(true)}>
            <span>Entregar en</span>
            <strong>{location?.city || "Elegir ubicación"}</strong>
          </button>
          <Link className="we-orders-link" to="/we-pedidos/pedidos">
            Mis pedidos
          </Link>
          <WePedidosAndroidLauncher compact />
          <form className="we-tracking-search" onSubmit={submitTracking}>
            <input value={trackingCode} onChange={(event) => setTrackingCode(event.target.value)} placeholder="Código de seguimiento" aria-label="Código de seguimiento" />
            <button type="submit" aria-label="Seguir pedido">→</button>
          </form>
          <button
            type="button"
            className="we-account-button"
            onClick={() => setAccountOpen(true)}
            aria-label="Abrir mi cuenta"
            title="Mi cuenta"
          >
            {authState.profile?.firstName?.slice(0, 1)?.toUpperCase() || "Mi"}
          </button>
        </div>
      </header>

      <div className="we-content">
        <section className="we-search-band">
          <div>
            <p className="we-eyebrow">Delivery en {location?.city || "tu ciudad"}</p>
            <h1>¿Qué querés pedir?</h1>
          </div>
          <div className="we-search-controls">
            <label className="we-search-field">
              <span aria-hidden="true">⌕</span>
              <input value={filters.search} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} placeholder="Buscar restaurante o comida" />
            </label>
            <button type="button" className="we-filter-button" onClick={() => setFiltersOpen(true)}>
              Filtros {activeFilterCount ? <span>{activeFilterCount}</span> : null}
            </button>
          </div>
        </section>

        {source === "local_published" ? <p className="we-environment-note">Vista local de sucursales publicadas</p> : null}
        {notice ? <div className="we-notice" role="status"><span>{notice}</span><button type="button" onClick={() => setNotice("")} aria-label="Cerrar aviso">×</button></div> : null}
        {error && !locationOpen ? <div className="we-error" role="alert">{error}</div> : null}

        {loading && !restaurants.length ? (
          <section className="we-loading" aria-live="polite">
            <img className="we-logo-image" src={mozzoIcon} alt="" />
            <p>Buscando locales disponibles...</p>
          </section>
        ) : filteredRestaurants.length || productMatches.length ? (
          <>
            {hasSearch && productMatches.length ? (
              <section className="we-section we-product-results-section">
                <div className="we-section-heading">
                  <div>
                    <h2>Platos encontrados</h2>
                    <p>Resultados de las cartas disponibles en tu zona</p>
                  </div>
                  <span>{productMatches.length}</span>
                </div>
                <div className="we-product-result-grid">
                  {productMatches.map((product) => (
                    <ProductSearchCard
                      key={`${product.restaurantId}-${product.id}`}
                      product={product}
                      onOpen={openProduct}
                    />
                  ))}
                </div>
              </section>
            ) : null}
            {!hasSearch ? (
              <>
                <RestaurantSection title="Cerca de vos" description="Ordenados por distancia desde tu ubicación" restaurants={sections.nearby} onOpen={openRestaurant} />
                <RestaurantSection title="Mejor valorados" description="Reputación, pedidos completados y cumplimiento" restaurants={sections.bestRated} onOpen={openRestaurant} />
              </>
            ) : null}
            <section className="we-section we-all-section">
              <div className="we-section-heading">
                <div><h2>{hasSearch ? "Restaurantes relacionados" : "Todos los restaurantes"}</h2><p>Locales disponibles en {location?.city}</p></div>
                <span>{sections.all.length}</span>
              </div>
              <div className="we-restaurant-grid">
                {sections.all.map((restaurant) => <RestaurantCard key={restaurant.id} restaurant={restaurant} onOpen={openRestaurant} />)}
              </div>
            </section>
          </>
        ) : location && !searchingProducts ? (
          <section className="we-empty-state">
            <img className="we-logo-image" src={mozzoIcon} alt="" />
            <h2>{hasSearch ? "No encontramos ese plato o restaurante" : "No hay locales publicados en esta zona"}</h2>
            <p>{hasSearch ? "Proba con otro nombre o una categoría más general." : "Cuando una sucursal registrada active We! pedidos aparecerá aquí, incluso si está cerrada."}</p>
            <button type="button" className="we-primary-button" onClick={() => setFilters(initialFilters)}>Limpiar filtros</button>
          </section>
        ) : searchingProducts ? <p className="we-searching-products" role="status">Buscando platos...</p> : null}
      </div>

      <nav className="we-bottom-nav" aria-label="Navegación de We! pedidos">
        <Link className="is-active" to="/we-pedidos"><span aria-hidden="true">⌂</span>Inicio</Link>
        <Link to="/we-pedidos/pedidos"><span aria-hidden="true">▤</span>Pedidos</Link>
        <button type="button" onClick={() => setLocationOpen(true)}><span aria-hidden="true">⌖</span>Ubicación</button>
        <button type="button" onClick={() => setAccountOpen(true)}><span aria-hidden="true">○</span>Mi cuenta</button>
      </nav>

      {locationOpen ? (
        <LocationDialog
          onClose={location ? () => { setLocationOpen(false); setError(""); } : null}
          onUseCurrentLocation={useCurrentLocation}
          onUseManualLocation={useManualLocation}
          locating={locating || loading}
          error={error}
          initialLocation={{
            city: location?.city || defaultAddress?.city || "Corrientes",
            address:
              defaultAddress?.addressLine ||
              `${defaultAddress?.street || ""} ${defaultAddress?.streetNumber || ""}`.trim(),
          }}
        />
      ) : null}
      {filtersOpen ? <div className="we-filter-backdrop" role="presentation"><FiltersPanel filters={filters} setFilters={setFilters} foodTypes={foodTypes} onClose={() => setFiltersOpen(false)} onClear={() => setFilters(initialFilters)} /></div> : null}
      {accountOpen ? (
        <WeCustomerAccountDialog
          profile={authState.profile}
          onClose={() => setAccountOpen(false)}
          onEditLocation={() => {
            setAccountOpen(false);
            setLocationOpen(true);
          }}
          onOpenOrders={() => navigate("/we-pedidos/pedidos")}
          onSignOut={handleSignOut}
          signingOut={signingOut}
        />
      ) : null}
    </main>
  );
}

export function WePedidosOrders() {
  useWePedidosBranding("Mis pedidos");
  const [authState, setAuthState] = useState({
    loading: true,
    session: null,
    profile: null,
    error: "",
  });
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState("");
  const [showOldOrders, setShowOldOrders] = useState(false);
  const notificationOrdersRef = useRef(null);
  const ordersRefreshInFlightRef = useRef(false);
  const ordersRefreshPendingRef = useRef(false);

  const handleReviewSubmitted = useCallback((orderId, review) => {
    setOrders((currentOrders) =>
      currentOrders.map((order) =>
        order.id === orderId ? { ...order, review, canReview: false } : order
      )
    );
  }, []);

  const handleProductReviewSubmitted = useCallback(
    (orderId, orderItemId, review) => {
      setOrders((currentOrders) =>
        currentOrders.map((order) =>
          order.id === orderId
            ? {
                ...order,
                items: (order.items || []).map((item) =>
                  item.id === orderItemId ? { ...item, review } : item
                ),
              }
            : order
        )
      );
    },
    []
  );

  const handleCustomerRequestSubmitted = useCallback(
    (orderId, customerRequest) => {
      setOrders((currentOrders) =>
        currentOrders.map((order) =>
          order.id === orderId ? { ...order, customerRequest } : order
        )
      );
    },
    []
  );

  const refreshAuthState = useCallback(async () => {
    try {
      const nextState = await getWeCustomerAuthState();
      setAuthState({ ...nextState, loading: false, error: "" });
    } catch (requestError) {
      setAuthState({
        loading: false,
        session: null,
        profile: null,
        error:
          requestError?.message ||
          "No pudimos recuperar tu cuenta de We! pedidos.",
      });
    }
  }, []);

  useEffect(() => {
    void refreshAuthState();
    return subscribeToWeCustomerAuth(() => {
      void refreshAuthState();
    });
  }, [refreshAuthState]);

  const accessToken = authState.session?.access_token || "";
  const accountKey =
    authState.profile?.userId || authState.profile?.email || "";
  const { visibleOrders, oldOrders } = useMemo(
    () => partitionWeCustomerOrders(orders),
    [orders]
  );
  const refreshOrders = useCallback(async ({ silent = false } = {}) => {
    if (!authState.session || !accountKey) return;
    if (ordersRefreshInFlightRef.current) {
      ordersRefreshPendingRef.current = true;
      return;
    }

    ordersRefreshInFlightRef.current = true;
    if (!silent) {
      setLoadingOrders(true);
      setOrdersError("");
    }
    try {
      do {
        ordersRefreshPendingRef.current = false;
        const response = await loadWeCustomerOrders({ accessToken, accountKey });
        const nextOrders = Array.isArray(response?.orders) ? response.orders : [];
        notifyCustomerOrderChanges(notificationOrdersRef.current, nextOrders);
        notificationOrdersRef.current = nextOrders;
        setOrders(nextOrders);
        void syncNativeCustomerBackground({
          accessToken,
          userId: authState.session?.user?.id || "",
          orders: nextOrders,
        });
      } while (ordersRefreshPendingRef.current);
    } catch (requestError) {
      if (!silent) {
        setOrdersError(
          requestError?.message || "No pudimos recuperar tus pedidos."
        );
      }
    } finally {
      ordersRefreshInFlightRef.current = false;
      if (!silent) setLoadingOrders(false);
    }
  }, [accessToken, accountKey, authState.session]);

  useEffect(() => {
    notificationOrdersRef.current = null;
    setShowOldOrders(false);
  }, [accountKey]);

  useEffect(() => {
    if (!authState.session || !accountKey) return undefined;
    void refreshOrders();
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        void refreshOrders({ silent: true });
      }
    };
    const refreshSilently = () => void refreshOrders({ silent: true });
    const intervalId = window.setInterval(refreshSilently, 4000);
    const unsubscribeRider = subscribeToLocalWeRiderUpdates(refreshSilently);
    const unsubscribeRemote = subscribeToWeCustomerOrderChanges(
      authState.session?.user?.id,
      refreshSilently
    );
    window.addEventListener("focus", refreshWhenVisible);
    window.addEventListener(WE_PEDIDOS_ORDERS_UPDATED_EVENT, refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.clearInterval(intervalId);
      unsubscribeRider();
      unsubscribeRemote();
      window.removeEventListener("focus", refreshWhenVisible);
      window.removeEventListener(WE_PEDIDOS_ORDERS_UPDATED_EVENT, refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [accountKey, authState.session, refreshOrders]);

  if (authState.loading) {
    return (
      <main className="we-app-shell">
        <section className="we-loading" aria-live="polite">
          <img className="we-logo-image" src={mozzoIcon} alt="" />
          <p>Recuperando tus pedidos...</p>
        </section>
      </main>
    );
  }

  if (!authState.session) {
    return (
      <>
        <WeCustomerAccess onAuthenticated={refreshAuthState} />
        {authState.error ? (
          <p className="we-access-global-error" role="alert">
            {authState.error}
          </p>
        ) : null}
      </>
    );
  }

  const renderOrderCard = (order) => (
    <article className="we-order-card" key={order.id}>
      <div className="we-order-card-head">
        <div>
          <p>{order.restaurantName}</p>
          <h2>{order.displayNumber}</h2>
          <time dateTime={order.createdAt}>
            {order.createdAt
              ? orderDateFormatter.format(new Date(order.createdAt))
              : "Fecha no disponible"}
          </time>
        </div>
        <span className={`we-order-status is-${order.statusTone || "active"}`}>
          {order.statusLabel}
        </span>
      </div>

      <ul className="we-order-items">
        {(order.items || []).map((item) => (
          <li key={item.id}>
            <span><strong>{item.quantity}×</strong> {item.productName}</span>
            <b>{moneyFormatter.format(Number(item.lineTotal || 0))}</b>
          </li>
        ))}
      </ul>

      <div className="we-order-payment">
        <span>{order.paymentMethodLabel}</span>
        <strong>{order.paymentStatusLabel}</strong>
      </div>
      <div className="we-order-total">
        <span>Total</span>
        <strong>{moneyFormatter.format(Number(order.total || 0))}</strong>
      </div>
      <DeliveryVerificationCode code={order.deliveryVerificationCode} />
      <WeOrderCustomerRequest
        order={order}
        accessToken={accessToken}
        accountKey={accountKey}
        onSubmitted={handleCustomerRequestSubmitted}
      />
      <WeOrderRestaurantRating
        order={order}
        accessToken={accessToken}
        accountKey={accountKey}
        onSubmitted={handleReviewSubmitted}
      />
      <WeOrderProductRatings
        order={order}
        accessToken={accessToken}
        accountKey={accountKey}
        onSubmitted={handleProductReviewSubmitted}
      />
      {order.trackingUrl && !["cancelled", "rejected"].includes(order.status) ? (
        <Link
          className="we-primary-button we-order-track"
          to={resolveWePedidosNavigationUrl(order.trackingUrl)}
        >
          Ver seguimiento
        </Link>
      ) : ["cancelled", "rejected"].includes(order.status) ? (
        <p className="we-order-tracking-pending">
          Este pedido fue cancelado y queda guardado en tu historial.
        </p>
      ) : (
        <p className="we-order-tracking-pending">
          El seguimiento aparecera cuando el restaurante tome el pedido.
        </p>
      )}
    </article>
  );

  return (
    <main className="we-orders-shell">
      <header className="we-orders-header">
        <Link className="we-orders-back" to="/we-pedidos" aria-label="Volver al inicio">
          ←
        </Link>
        <Link className="we-brand" to="/we-pedidos" aria-label="We! pedidos, inicio">
          <img className="we-logo-image" src={mozzoIcon} alt="" />
          <span><strong>We!</strong> pedidos<small>por MozzoApp</small></span>
        </Link>
        <button
          type="button"
          className="we-secondary-button we-orders-refresh"
          onClick={() => void refreshOrders()}
          disabled={loadingOrders}
        >
          {loadingOrders ? "Actualizando..." : "Actualizar"}
        </button>
      </header>

      <div className="we-orders-content">
        <section className="we-orders-heading">
          <p className="we-eyebrow">Tu cuenta We!</p>
          <h1>Mis pedidos</h1>
          <p>Historial de {authState.profile?.fullName || "tu cuenta"}</p>
        </section>

        {ordersError ? <div className="we-error" role="alert">{ordersError}</div> : null}
        {loadingOrders && !orders.length ? (
          <section className="we-loading" aria-live="polite">
            <img className="we-logo-image" src={mozzoIcon} alt="" />
            <p>Buscando tus pedidos...</p>
          </section>
        ) : null}
        {!loadingOrders && !ordersError && !orders.length ? (
          <section className="we-orders-empty">
            <img className="we-logo-image" src={mozzoIcon} alt="" />
            <h2>Todavia no hiciste pedidos</h2>
            <p>Cuando confirmes uno desde We!, aparecera aca automaticamente.</p>
            <Link className="we-primary-button" to="/we-pedidos">Buscar restaurantes</Link>
          </section>
        ) : null}

        {visibleOrders.length ? (
          <section className="we-orders-list" aria-live="polite">
            {visibleOrders.map(renderOrderCard)}
          </section>
        ) : null}

        {oldOrders.length ? (
          <section className="we-old-orders">
            <button
              type="button"
              className="we-old-orders-toggle"
              onClick={() => setShowOldOrders((current) => !current)}
              aria-expanded={showOldOrders}
              aria-controls="we-old-orders-list"
            >
              <span>Pedidos antiguos</span>
              <strong>{oldOrders.length}</strong>
              <span className="we-old-orders-chevron" aria-hidden="true">
                {showOldOrders ? "▲" : "▼"}
              </span>
            </button>
            {showOldOrders ? (
              <div
                id="we-old-orders-list"
                className="we-orders-list we-old-orders-list"
                aria-live="polite"
              >
                {oldOrders.map(renderOrderCard)}
              </div>
            ) : null}
          </section>
        ) : null}
      </div>
    </main>
  );
}

const trackingSteps = [
  "waiting_restaurant",
  "searching_driver",
  "driver_assigned",
  "picked_up",
  "in_transit",
  "delivered",
];
const TRACKING_STEP_INDEX = Object.freeze({
  waiting_restaurant: 0,
  searching_driver: 1,
  driver_assigned: 2,
  arriving_pickup: 2,
  ready_for_pickup: 2,
  arrived_pickup: 2,
  picked_up: 3,
  in_transit: 4,
  delivered: 5,
});
const TRACKING_REFRESH_INTERVAL_MS = 4000;
const TERMINAL_TRACKING_STATUSES = new Set([
  "delivered",
  "cancelled",
  "rejected",
  "refunded",
]);

const vehicleLabel = (vehicleType) => ({
  motorcycle: "Moto",
  bicycle: "Bicicleta",
  car: "Auto",
  walking: "A pie",
}[vehicleType] || vehicleType || "Vehículo");

export function WePedidosTracking() {
  useWePedidosBranding("Seguimiento");
  const { trackingToken = "" } = useParams();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [customerUserId, setCustomerUserId] = useState("");
  const notificationTrackingRef = useRef(null);
  const trackingStatus = tracking?.status || "";

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getWeCustomerAuthState()
      .then(async (state) => {
        const nextAccessToken = state?.session?.access_token || "";
        const nextCustomerUserId = state?.session?.user?.id || "";
        if (mounted) {
          setAccessToken(nextAccessToken);
          setCustomerUserId(nextCustomerUserId);
        }
        const response = await loadWeDeliveryTracking(trackingToken, {
          accessToken: nextAccessToken,
        });
        if (response?.tracking) {
          void syncNativeCustomerBackground({
            accessToken: nextAccessToken,
            userId: nextCustomerUserId,
            orders: [
              {
                id: response.tracking.orderId || trackingToken,
                trackingStatus: response.tracking.status,
              },
            ],
          });
        }
        return response;
      })
      .then((response) => {
        if (mounted) {
          const nextTracking = response?.tracking || null;
          notificationTrackingRef.current = nextTracking;
          setTracking(nextTracking);
        }
      })
      .catch((requestError) => {
        if (mounted) setError(requestError?.message || "No pudimos abrir el seguimiento.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, [trackingToken]);

  useEffect(() => {
    if (!trackingStatus || TERMINAL_TRACKING_STATUSES.has(trackingStatus)) {
      return undefined;
    }

    let mounted = true;
    let refreshInFlight = false;
    let refreshPending = false;
    const refreshTracking = async () => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden" &&
        typeof window.MozzoNotifications?.notify !== "function"
      ) {
        return;
      }

      if (refreshInFlight) {
        refreshPending = true;
        return;
      }

      refreshInFlight = true;
      try {
        do {
          refreshPending = false;
          const response = await loadWeDeliveryTracking(trackingToken, {
            accessToken,
          });
          if (mounted) {
            const nextTracking = response?.tracking || null;
            notifyCustomerTrackingChange(
              notificationTrackingRef.current,
              nextTracking,
              trackingToken
            );
            notificationTrackingRef.current = nextTracking;
            setTracking(nextTracking);
            setError("");
            void syncNativeCustomerBackground({
              accessToken,
              userId: customerUserId,
              orders: [
                {
                  id: nextTracking?.orderId || trackingToken,
                  trackingStatus: nextTracking?.status,
                },
              ],
            });
          }
        } while (mounted && refreshPending);
      } catch {
        // Keep the latest known state during a temporary network interruption.
      } finally {
        refreshInFlight = false;
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") void refreshTracking();
    };
    const intervalId = window.setInterval(
      refreshTracking,
      TRACKING_REFRESH_INTERVAL_MS
    );
    const unsubscribeRider = subscribeToLocalWeRiderUpdates(refreshTracking);
    const unsubscribeRemote = subscribeToWeCustomerOrderChanges(
      customerUserId,
      refreshTracking
    );

    window.addEventListener("focus", refreshTracking);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      mounted = false;
      window.clearInterval(intervalId);
      unsubscribeRider();
      unsubscribeRemote();
      window.removeEventListener("focus", refreshTracking);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [accessToken, customerUserId, trackingStatus, trackingToken]);

  const currentStepIndex = TRACKING_STEP_INDEX[tracking?.status] ?? 0;

  return (
    <main className="we-tracking-shell">
      <header className="we-tracking-header">
        <Link to="/we-pedidos" aria-label="Volver a We! pedidos">←</Link>
        <div className="we-brand"><img className="we-logo-image" src={mozzoIcon} alt="" /><span><strong>We!</strong> pedidos<small>Seguimiento</small></span></div>
      </header>
      {loading ? <section className="we-loading"><img className="we-logo-image" src={mozzoIcon} alt="" /><p>Actualizando tu entrega...</p></section> : null}
      {error ? <section className="we-tracking-error"><h1>No pudimos abrir el seguimiento</h1><p>{error}</p><Link to="/we-pedidos">Volver al inicio</Link></section> : null}
      {tracking ? (
        <div className="we-tracking-content">
          <section className="we-tracking-summary" aria-live="polite">
            <p className="we-eyebrow">Pedido {tracking.displayNumber}</p>
            <h1>{tracking.statusLabel}</h1>
            <p>{tracking.customerMessage}</p>
            <strong>{tracking.restaurantName}</strong>
            {tracking.estimatedArrivalAt ? <span>Hora estimada: {new Date(tracking.estimatedArrivalAt).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}</span> : null}
          </section>
          <ol className="we-progress" aria-label="Progreso de la entrega">
            {trackingSteps.map((step, index) => <li key={step} className={index <= currentStepIndex ? "is-complete" : ""}><span>{index < currentStepIndex ? "✓" : index + 1}</span><p>{({ waiting_restaurant: "Confirmación", searching_driver: "Asignación", driver_assigned: "Repartidor", picked_up: "Retiro", in_transit: "En camino", delivered: "Entrega" })[step]}</p></li>)}
          </ol>
          <DeliveryVerificationCode code={tracking.deliveryVerificationCode} />
          {tracking.driver ? <section className="we-driver-band"><div className="we-driver-avatar">{tracking.driver.firstName?.slice(0, 1)}</div><div><span>Tu repartidor</span><strong>{tracking.driver.firstName}</strong><small>{vehicleLabel(tracking.driver.vehicleType)}{tracking.driver.vehicleReference ? ` · ${tracking.driver.vehicleReference}` : ""}</small></div></section> : null}
          <section className="we-tracking-events"><h2>Actividad</h2>{tracking.events?.map((event, index) => <article key={`${event.status}-${event.createdAt}-${index}`}><span></span><div><strong>{event.label}</strong><small>{new Date(event.createdAt).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}</small>{event.note ? <p>{event.note}</p> : null}</div></article>)}</section>
        </div>
      ) : null}
    </main>
  );
}
